import { FlexGrid2, Stacker, Styler, TextField, TypographyBody, usePrevious } from '@honeycomb/ui-core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGlobalContext } from '@honeycomb/data';
import { FilterAccordion } from './FilterAccordion';
import { applyFormatString } from './utils';
import { useDebounce } from '../../utils/useDebounce';
import { formatCurrency } from '../../utils/formatCurrency';
let minAbort = false;
let maxAbort = false;
export function BudgetFilter({ id, heading, subHeading, min, max, formatString = '{s}', lower: lowerProp, upper: upperProp, onChange, }) {
    const { resourceStrings: { HC_BUDGET_FILTER_MIN_VAL_HIGHER_ERROR, HC_BUDGET_FILTER_MAX_VAL_LOWER_ERROR, HC_BUDGET_FILTER_MIN_VAL_LOW_ERROR, HC_BUDGET_FILTER_MAX_VAL_HIGH_ERROR, HC_MIN, HC_MAX, HC_RANGE, }, currentContext: { currencyCode, cultureCode }, } = useGlobalContext();
    const formattedCurrency = useMemo(() => {
        var _a, _b;
        const formatted = formatCurrency({ amount: 0, cultureCode, currencyCode });
        if (formatted) {
            return ((_a = formatted.split('0')) === null || _a === void 0 ? void 0 : _a[0]) ? (_b = formatted.split('0')) === null || _b === void 0 ? void 0 : _b[0] : '';
        }
        return '';
    }, [cultureCode, currencyCode]);
    // if we have the currency symbol of 1 char then we show that in the field with price else we show it in the label.
    const showPriceRangeCurrencyInField = formattedCurrency.length === 1;
    const [lower, setLower] = useState(lowerProp);
    const [upper, setUpper] = useState(upperProp);
    const prevLowerProp = usePrevious(lowerProp);
    const prevUpperProp = usePrevious(upperProp);
    const [minError, setMinError] = useState('');
    const [maxError, setMaxError] = useState('');
    const fieldLabelFormatString = showPriceRangeCurrencyInField ? formatString : '{s}';
    const formatLabel = useCallback((value, fieldLabelFormat) => applyFormatString(value, fieldLabelFormat), []);
    const debouncedOnChange = useDebounce(({ newLower, newUpper, group }) => {
        if (!(minAbort || maxAbort)) {
            onChange(newLower, newUpper, group);
        }
    }, 3000);
    const getMinError = () => {
        if (lower === 0 || lower >= upper) {
            minAbort = true;
            return HC_BUDGET_FILTER_MIN_VAL_HIGHER_ERROR;
        }
        if (lower < min) {
            minAbort = true;
            return HC_BUDGET_FILTER_MIN_VAL_LOW_ERROR;
        }
        minAbort = false;
        return '';
    };
    const getMaxError = () => {
        if (upper > max) {
            maxAbort = true;
            return HC_BUDGET_FILTER_MAX_VAL_HIGH_ERROR;
        }
        if (upper === 0 || upper <= lower) {
            maxAbort = true;
            return HC_BUDGET_FILTER_MAX_VAL_LOWER_ERROR;
        }
        maxAbort = false;
        return '';
    };
    useEffect(() => {
        setMinError(getMinError());
        setMaxError(getMaxError());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lower, upper]);
    useEffect(() => {
        if (lowerProp !== prevLowerProp) {
            setLower(lowerProp);
        }
        if (upperProp !== prevUpperProp) {
            setUpper(upperProp);
        }
    }, [lowerProp, upperProp, prevLowerProp, prevUpperProp]);
    const handleChange = (event) => {
        const { value } = event.target;
        const regex = /[\D]/g;
        const res = value.replace(regex, '');
        const num = res ? parseInt(res) : 0;
        if (event.target.name === 'min') {
            setLower(Number(num));
            debouncedOnChange({ newLower: num, newUpper: upper, group: id });
        }
        else {
            setUpper(Number(num));
            debouncedOnChange({ newLower: lower, newUpper: num, group: id });
        }
    };
    const priceFieldlabelExtension = showPriceRangeCurrencyInField ? '' : ` (${currencyCode})`;
    const customHeading = (React.createElement(Styler, null,
        React.createElement("span", null, heading),
        subHeading && (React.createElement(TypographyBody, { component: "span", size: "xs" }, ` (${subHeading})`))));
    return (React.createElement(FilterAccordion, { id: "BudgetFilter", heading: customHeading },
        React.createElement(FlexGrid2, { container: true, alignItems: "center", justifyContent: "left", flexWrap: "nowrap", spacing: 2 },
            React.createElement(FlexGrid2, { xs: "auto" },
                React.createElement(TextField, { error: Boolean(minError), name: "min", onChange: handleChange, value: lower > 0 ? formatLabel(lower, fieldLabelFormatString) : '', variant: "alternative", label: `${HC_MIN + priceFieldlabelExtension}`, width: 100 })),
            React.createElement(FlexGrid2, { xs: "hold" }, "-"),
            React.createElement(FlexGrid2, { xs: "auto" },
                React.createElement(TextField, { error: Boolean(maxError), onChange: handleChange, name: "max", value: upper > 0 ? formatLabel(upper, fieldLabelFormatString) : '', variant: "alternative", label: `${HC_MAX + priceFieldlabelExtension}`, width: 100 }))),
        React.createElement(TypographyBody, { size: "xs", weight: "semiBold", color: "system.error.base", mt: 2 }, minError),
        React.createElement(TypographyBody, { size: "xs", weight: "semiBold", color: "system.error.base", mt: 2 }, maxError),
        React.createElement(Stacker, { direction: "row", mt: 2, spacing: 1, flexWrap: "wrap" },
            React.createElement(TypographyBody, { size: "s", color: "grey.tint.500" },
                HC_RANGE,
                ":"),
            React.createElement(TypographyBody, { size: "s", color: "grey.tint.500", weight: "semiBold" },
                formatLabel(min, formatString),
                " - ",
                formatLabel(max, formatString)))));
}
